import { decode, infinity } from '../Duration/dist/effect-Duration.esm.js';
import { equals, symbol as symbol$1 } from '../Equal/dist/effect-Equal.esm.js';
import { dual, pipe } from '../Function/dist/effect-Function.esm.js';
import { symbol, combine, hash, string } from '../Hash/dist/effect-Hash.esm.js';
import { empty, get as get$2, has, set, remove, values } from '../HashMap/dist/effect-HashMap.esm.js';
import { map, context, fiberId, sync, flatMap, forEachSequentialDiscard, suspend, uninterruptibleMask, deferredUnsafeMake, provideContext, matchCauseEffect, zipRight, deferredFailCause, failCause, as, deferredSucceed, deferredAwait, succeed, forEachSequential } from './core.esm.js';
import { all, scopeMake, scopeExtend } from './fiberRuntime.esm.js';
import { get as get$1, makeWithTTL as makeWithTTL$1 } from './pool.esm.js';
import { make as make$1, get as get$3, update } from '../MutableRef/dist/effect-MutableRef.esm.js';
import { getOrUndefined, getOrElse, none, some } from '../Option/dist/effect-Option.esm.js';
import { pipeArguments } from '../Pipeable/dist/effect-Pipeable.esm.js';
import { isTagged } from '../Predicate/dist/effect-Predicate.esm.js';

/** @internal */
const KeyedPoolSymbolKey = "effect/KeyedPool";

/** @internal */
const KeyedPoolTypeId = /*#__PURE__*/Symbol.for(KeyedPoolSymbolKey);
const KeyedPoolMapValueSymbol = /*#__PURE__*/Symbol.for("effect/KeyedPool/MapValue");
const keyedPoolVariance = {
  _K: _ => _,
  _E: _ => _,
  _A: _ => _
};
class KeyedPoolImpl {
  [KeyedPoolTypeId] = keyedPoolVariance;
  constructor(getOrCreatePool, activePools) {
    this.getOrCreatePool = getOrCreatePool;
    this.activePools = activePools;
  }
  get(key) {
    return flatMap(this.getOrCreatePool(key), get$1);
  }
  invalidate(item) {
    return flatMap(this.activePools(), forEachSequentialDiscard(pool => pool.invalidate(item)));
  }
  pipe() {
    return pipeArguments(this, arguments);
  }
}
class Complete {
  _tag = "Complete";
  [KeyedPoolMapValueSymbol] = KeyedPoolMapValueSymbol;
  constructor(pool) {
    this.pool = pool;
  }
  [symbol]() {
    return pipe(string("effect/KeyedPool/Complete"), combine(hash(this.pool)));
  }
  [symbol$1](u) {
    return isComplete(u) && equals(this.pool, u.pool);
  }
}
const isComplete = u => isTagged(u, "Complete") && KeyedPoolMapValueSymbol in u;
class Pending {
  _tag = "Pending";
  [KeyedPoolMapValueSymbol] = KeyedPoolMapValueSymbol;
  constructor(deferred) {
    this.deferred = deferred;
  }
  [symbol]() {
    return pipe(string("effect/KeyedPool/Pending"), combine(hash(this.deferred)));
  }
  [symbol$1](u) {
    return isPending(u) && equals(this.deferred, u.deferred);
  }
}
const isPending = u => isTagged(u, "Pending") && KeyedPoolMapValueSymbol in u;
const makeImpl = (get, min, max, timeToLive) => pipe(all([context(), fiberId, sync(() => make$1(empty())), scopeMake()]), map(([context, fiberId, map, scope]) => {
  const getOrCreatePool = key => suspend(() => {
    let value = getOrUndefined(get$2(get$3(map), key));
    if (value === undefined) {
      return uninterruptibleMask(restore => {
        const deferred = deferredUnsafeMake(fiberId);
        value = new Pending(deferred);
        let previous = undefined;
        if (has(get$3(map), key)) {
          previous = getOrUndefined(get$2(get$3(map), key));
        } else {
          update(map, set(key, value));
        }
        if (previous === undefined) {
          return pipe(restore(scopeExtend(makeWithTTL$1({
            acquire: provideContext(get(key), context),
            min: min(key),
            max: max(key),
            timeToLive: getOrElse(timeToLive(key), () => infinity)
          }), scope)), matchCauseEffect({
            onFailure: cause => {
              const current = getOrUndefined(get$2(get$3(map), key));
              if (equals(current, value)) {
                update(map, remove(key));
              }
              return zipRight(deferredFailCause(deferred, cause), failCause(cause));
            },
            onSuccess: pool => {
              update(map, set(key, new Complete(pool)));
              return as(deferredSucceed(deferred, pool), pool);
            }
          }));
        }
        switch (previous._tag) {
          case "Complete":
            {
              return succeed(previous.pool);
            }
          case "Pending":
            {
              return restore(deferredAwait(previous.deferred));
            }
        }
      });
    }
    switch (value._tag) {
      case "Complete":
        {
          return succeed(value.pool);
        }
      case "Pending":
        {
          return deferredAwait(value.deferred);
        }
    }
  });
  const activePools = () => suspend(() => forEachSequential(Array.from(values(get$3(map))), value => {
    switch (value._tag) {
      case "Complete":
        {
          return succeed(value.pool);
        }
      case "Pending":
        {
          return deferredAwait(value.deferred);
        }
    }
  }));
  return new KeyedPoolImpl(getOrCreatePool, activePools);
}));

/** @internal */
const make = options => makeImpl(options.acquire, () => options.size, () => options.size, () => none());

/** @internal */
const makeWith = options => makeImpl(options.acquire, options.size, options.size, () => none());

/** @internal */
const makeWithTTL = options => {
  const timeToLive = decode(options.timeToLive);
  return makeImpl(options.acquire, options.min, options.max, () => some(timeToLive));
};

/** @internal */
const makeWithTTLBy = options => makeImpl(options.acquire, options.min, options.max, key => some(decode(options.timeToLive(key))));

/** @internal */
const get = /*#__PURE__*/dual(2, (self, key) => self.get(key));

/** @internal */
const invalidate = /*#__PURE__*/dual(2, (self, item) => self.invalidate(item));

export { KeyedPoolTypeId, get, invalidate, make, makeWith, makeWithTTL, makeWithTTLBy };
